/**
 * MSG-7: хранилище чата в IndexedDB (дизайн §6: история и служебное
 * состояние живут в браузере; сервер хранит только шифротекст).
 *
 * Три стора:
 * - messages: ключ [conversationId, messageId] — идемпотентность записи
 *   бесплатно (дубль догона перезапишет ту же строку), индекс bySeq для
 *   хронологии;
 * - outbox: неподтверждённые исходящие ЦЕЛИКОМ с конвертом (урок MSG-6 №1:
 *   message_id создаётся до первой попытки и живёт с конвертом — после
 *   перезагрузки вкладки есть что ретраить, сервер дедупит по message_id);
 * - kv: identity визави для TOFU, курсор последнего seq, passphrase стора.
 *
 * Собственнных сообщений расшифровать нельзя (Olm — только адресат), поэтому
 * исходящий plaintext сохраняется здесь при отправке — как MsgDb на Android.
 *
 * API промисное, без зависимостей. Класс принимает фабрику indexedDB — в
 * jsdom тестов её нет, тесты подставляют fake.
 */
const DB_NAME = 'total-msg';
// v2 — QR-2: два стора комнаты. 🔴 DB_NAME, keyPath и методы версии 1 НЕ
// меняются: переписка 1:1 работает на бою с 25.08, и сломать её миграцией —
// значит сломать то, чего комната ещё даже не умеет.
const DB_VERSION = 2;

function reqAsPromise(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Схема по версиям — ЧИСТАЯ функция, чтобы её можно было проверить тестом без
 * IndexedDB (в jsdom его нет, а fake-indexeddb в зависимостях проекта тоже).
 *
 * 🔴 Ступени идут по `oldVersion`, а не «создать всё». Прежний обработчик
 * создавал три стора безусловно — на уже существующей базе версии 1 это
 * ConstraintError «стор уже есть», то есть чат перестал бы открываться у
 * КАЖДОГО, кто им пользовался. Новая база приходит с `oldVersion === 0` и
 * проходит обе ступени подряд.
 *
 * @param db объект с createObjectStore (настоящий IDBDatabase или заглушка)
 * @param oldVersion версия, С КОТОРОЙ обновляемся; 0 — базы не было
 */
export function applyUpgrade(db, oldVersion) {
  if (oldVersion < 1) {
    const messages = db.createObjectStore('messages', {
      keyPath: ['conversationId', 'messageId'],
    });
    messages.createIndex('bySeq', ['conversationId', 'seq']);
    db.createObjectStore('outbox', { keyPath: 'messageId' });
    db.createObjectStore('kv');
  }
  if (oldVersion < 2) {
    // QR-2. Свои сторы, а не общие с 1:1: id комнаты и id разговора — разного
    // рода, и одна общая таблица означала бы, что перепутать их можно молча.
    const roomMessages = db.createObjectStore('roomMessages', {
      keyPath: ['roomId', 'messageId'],
    });
    // Ряд номеров устройства РАЗРЕЖЕН (свои сообщения, чужие «до входа», дыры
    // ретеншна) — индекс нужен для порядка, а не для непрерывности.
    roomMessages.createIndex('byRoomSeq', ['roomId', 'roomSeq']);
    db.createObjectStore('roomOutbox', { keyPath: 'messageId' });
  }
}

export class MsgStore {
  constructor(idbFactory = globalThis.indexedDB) {
    this.idb = idbFactory;
    this.db = null;
  }

  async open() {
    const req = this.idb.open(DB_NAME, DB_VERSION);
    // `event.oldVersion` — единственный источник правды о том, что уже есть в
    // базе. Без него обновление существующей базы падает на первом же сторе.
    req.onupgradeneeded = (event) => applyUpgrade(req.result, event.oldVersion);
    this.db = await new Promise((resolve, reject) => {
      let settled = false;
      req.onsuccess = () => {
        // 🔴 Поздний успех ПОСЛЕ blocked: соседняя вкладка закрылась, база
        // открылась — но владельца у этого соединения уже нет, и держать его
        // значит самому стать той помехой, из-за которой мы только что
        // отказали (ревью В1 р.2, Medium).
        if (settled) {
          try { req.result.close(); } catch { /* уже закрыта */ }
          return;
        }
        settled = true;
        resolve(req.result);
      };
      req.onerror = () => {
        if (settled) return;
        settled = true;
        reject(req.error);
      };
      // 🔴 `blocked` — соседняя вкладка держит СТАРУЮ версию базы и не даёт
      // обновиться. Без этой ветки промис не резолвится и не реджектится
      // ВООБЩЕ: страница висит без ошибки и без чата. Случай не редкий — это
      // ровно день выкатки, когда одна вкладка ещё на версии 1 (ревью В1
      // р.1 проход Б, High).
      req.onblocked = () => {
        if (settled) return;
        settled = true;
        reject(new Error(
          'Хранилище чата занято другой вкладкой этого сайта — '
          + 'закройте её и обновите страницу.'));
      };
    });
    // И симметрично: сами держать обновление соседней вкладке мы не вправе.
    this.db.onversionchange = () => {
      try { this.db?.close(); } catch { /* уже закрыта — и хорошо */ }
    };
    return this;
  }

  close() {
    this.db?.close();
    this.db = null;
  }

  _store(name, mode) {
    return this.db.transaction(name, mode).objectStore(name);
  }

  /**
   * @param m {conversationId, messageId, seq, direction: 'in'|'out',
   *           text: string|null (null = не расшифровано), sentAt}
   */
  putMessage(m) {
    return reqAsPromise(this._store('messages', 'readwrite').put(m));
  }

  getMessage(conversationId, messageId) {
    return reqAsPromise(
      this._store('messages', 'readonly').get([conversationId, messageId]));
  }

  /** Хронология разговора по seq (криптослой выдаёт плотный серверный seq). */
  async listMessages(conversationId) {
    const idx = this._store('messages', 'readonly').index('bySeq');
    const range = globalThis.IDBKeyRange
      ? globalThis.IDBKeyRange.bound([conversationId, -Infinity], [conversationId, Infinity])
      : null;
    const rows = await reqAsPromise(idx.getAll(range));
    return rows.sort((a, b) => a.seq - b.seq);
  }

  putOutbox(entry) {
    return reqAsPromise(this._store('outbox', 'readwrite').put(entry));
  }

  deleteOutbox(messageId) {
    return reqAsPromise(this._store('outbox', 'readwrite').delete(messageId));
  }

  async listOutbox(conversationId) {
    const rows = await reqAsPromise(this._store('outbox', 'readonly').getAll());
    return rows
      .filter((r) => r.conversationId === conversationId)
      .sort((a, b) => a.sentAt - b.sentAt);
  }

  // --- QR-2: общая комната ---------------------------------------------------
  // Форма строки повторяет 1:1, но несёт то, чего у 1:1 нет: КТО отправил
  // (в комнате собеседник не один) и разреженный roomSeq.

  /**
   * @param m {roomId, messageId, roomSeq, direction: 'in'|'out',
   *           text: string|null, sentAt,
   *           senderType, senderId, senderDeviceId,
   *           attachments: array|null}  // ATT С3: метаданные с ключами
   */
  putRoomMessage(m) {
    return reqAsPromise(this._store('roomMessages', 'readwrite').put(m));
  }

  getRoomMessage(roomId, messageId) {
    return reqAsPromise(
      this._store('roomMessages', 'readonly').get([roomId, messageId]));
  }

  /** Хронология комнаты. Порядок по roomSeq, дыры в ряду — норма. */
  async listRoomMessages(roomId) {
    const idx = this._store('roomMessages', 'readonly').index('byRoomSeq');
    const range = globalThis.IDBKeyRange
      ? globalThis.IDBKeyRange.bound([roomId, -Infinity], [roomId, Infinity])
      : null;
    const rows = await reqAsPromise(idx.getAll(range));
    return rows.sort((a, b) => a.roomSeq - b.roomSeq);
  }

  /**
   * @param entry {roomId, messageId, text, sentAt, attempts,
   *               state: 'pending'|'blocked_keys'|'failed_terminal',
   *               copies: [{recipientDeviceId, cipher, state}]}
   * Живёт до подтверждения сервером: message_id рождается ДО первой попытки,
   * поэтому перезагрузка вкладки не теряет ни текст, ни готовые шифротексты.
   */
  putRoomOutbox(entry) {
    return reqAsPromise(this._store('roomOutbox', 'readwrite').put(entry));
  }

  deleteRoomOutbox(messageId) {
    return reqAsPromise(this._store('roomOutbox', 'readwrite').delete(messageId));
  }

  async listRoomOutbox(roomId) {
    const rows = await reqAsPromise(this._store('roomOutbox', 'readonly').getAll());
    return rows
      .filter((r) => r.roomId === roomId)
      .sort((a, b) => a.sentAt - b.sentAt);
  }

  getKV(key) {
    return reqAsPromise(this._store('kv', 'readonly').get(key));
  }

  setKV(key, value) {
    return reqAsPromise(this._store('kv', 'readwrite').put(value, key));
  }
}
